import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { scrollToId, PHONE_DISPLAY, PHONE_TEL } from "@/lib/scroll";

const NAV = [
  { id: "start", label: "START" },
  { id: "leistungen", label: "LEISTUNGEN" },
  { id: "ueber-uns", label: "ÜBER UNS" },
  { id: "warum-wir", label: "WARUM WIR" },
  { id: "kontakt", label: "KONTAKT" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled || open
            ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto max-w-7xl px-5 md:px-8 h-[72px] flex items-center justify-between gap-4">
          <button
            data-testid="nav-logo"
            onClick={() => go("start")}
            className="flex items-center gap-3 shrink-0"
            aria-label="TERBECK SERVICE – zur Startseite"
          >
            <img
              src="/assets/terbeck-logo.jpeg"
              alt="TERBECK SERVICE Logo – Transport & Logistik Gronau"
              className="h-10 w-10 rounded-md object-cover ring-1 ring-white/15"
            />
            <span className="font-display font-bold tracking-tight text-white text-sm sm:text-base leading-none">
              TERBECK <span className="text-brand-bright">SERVICE</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Hauptnavigation">
            {NAV.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-link-${item.id}`}
                onClick={() => go(item.id)}
                className="text-xs font-semibold tracking-[0.18em] text-white/70 hover:text-brand-bright transition-colors duration-300"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-phone-link"
              href={PHONE_TEL}
              className="hidden xl:flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-brand-bright transition-colors duration-300"
            >
              <Phone className="h-4 w-4 text-brand-bright" />
              {PHONE_DISPLAY}
            </a>
            <button
              data-testid="nav-cta-button"
              onClick={() => go("kontakt")}
              className="hidden sm:inline-flex rounded-full bg-brand px-5 py-2.5 text-xs font-bold tracking-wider text-black hover:bg-brand-bright transition-colors duration-300"
            >
              ANGEBOT ANFRAGEN
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white"
              aria-label={open ? "Menü schließen" : "Menü öffnen"}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink/95 backdrop-blur-xl lg:hidden flex flex-col justify-center px-8"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile Navigation">
              {NAV.map((item, i) => (
                <motion.button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.45 }}
                  onClick={() => go(item.id)}
                  className="text-left font-display text-3xl font-bold uppercase tracking-tight text-white hover:text-brand-bright transition-colors duration-300 py-2"
                >
                  <span className="text-brand-bright text-sm align-super mr-3">0{i + 1}</span>
                  {item.label}
                </motion.button>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.45 }}
              className="mt-10 flex flex-col gap-4"
            >
              <button
                data-testid="mobile-cta-button"
                onClick={() => go("kontakt")}
                className="w-full rounded-full bg-brand py-4 text-sm font-bold tracking-wider text-black"
              >
                ANGEBOT ANFRAGEN
              </button>
              <a
                data-testid="mobile-phone-link"
                href={PHONE_TEL}
                className="w-full rounded-full border border-white/20 py-4 text-center text-sm font-bold tracking-wider text-white"
              >
                {PHONE_DISPLAY}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
